import { useState } from 'react';
import {
  Container,
  CloseButton,
  Nav,
  NavList,
  Link,
  InternalLink,
  CTA,
} from './styled';
import {
  ROUTE_DASHBOARD,
  ROUTE_PLANS,
  ROUTE_ABOUT,
  ROUTE_WELCOME,
} from '@/constants/routes';
import { Modal } from '@simplywallst/ui-core';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';
import If from '@/components/If';
import { Transition } from 'react-transition-group';
import useUser from '@/hooks/useUser';
import { useTranslation } from '@/hooks/useTranslation';

const GuestRegistrationModal = loadable(
  () =>
    import(/* webpackPrefetch: true */ '@/components/GuestRegistrationModal')
);

interface Props {
  onClose?: () => void;
  show: boolean;
}

export const SideMenu = ({ show, onClose }: Props) => {
  const [showRegistration, setShowRegistration] = useState(false);
  const { trackEvent } = useTracking();
  const {
    data: { loggedIn },
  } = useUser();
  const t = useTranslation();
  return (
    <>
      <Transition in={show} timeout={300}>
        {(status) => {
          if (status === 'exited') return null;
          return (
            <Modal isNaked removeWindowScroll onClose={onClose}>
              <Container status={status}>
                <CloseButton onClick={onClose} />
                <Nav>
                  <NavList>
                    <InternalLink to={{ type: ROUTE_ABOUT }}>
                      {t('app.topbar.publicNav.aboutUs')}
                    </InternalLink>
                  </NavList>
                  <NavList>
                    <InternalLink to={{ type: ROUTE_PLANS }}>
                      {t('app.topbar.publicNav.plans')}
                    </InternalLink>
                  </NavList>
                  <NavList>
                    <Link href="/careers">
                      {t('app.topbar.publicNav.careers')}
                    </Link>
                  </NavList>
                </Nav>
                <If
                  conditions={{
                    loggedOut: !loggedIn,
                    loggedIn: loggedIn,
                  }}
                >
                  {{
                    loggedOut: () => (
                      <>
                        <CTA
                          styleType="louderLarge"
                          display="block"
                          onClick={() => setShowRegistration(true)}
                        >
                          {t('app.topbar.publicNav.registerForFreePlan')}
                        </CTA>
                        <InternalLink
                          to={{ type: ROUTE_WELCOME }}
                          onClick={() => {
                            trackEvent({
                              action: 'click',
                              modifier: 'login',
                            });
                          }}
                        >
                          <CTA styleType="gentleLargeReverse" display="block">
                            {t('app.topbar.publicNav.login')}
                          </CTA>
                        </InternalLink>
                      </>
                    ),
                    loggedIn: () => (
                      <InternalLink
                        to={{ type: ROUTE_DASHBOARD }}
                        onClick={() => {
                          trackEvent({
                            action: 'click',
                            modifier: 'dashboard',
                          });
                        }}
                      >
                        <CTA styleType="louderLarge" display="block">
                          {t('app.topbar.publicNav.goToPlatform')}
                        </CTA>
                      </InternalLink>
                    ),
                  }}
                </If>
              </Container>
            </Modal>
          );
        }}
      </Transition>

      <If match={showRegistration}>
        {() => (
          <GuestRegistrationModal
            canClose
            onClose={() => setShowRegistration(false)}
            onRegisterSuccess={() => {
              trackEvent({
                type: 'track',
                subject: 'sideMenuMobile',
                modifier: 'registration',
                action: 'success',
              });
            }}
          />
        )}
      </If>
    </>
  );
};
